import { useEffect, useState, useCallback } from "react";
import { api, API, getToken } from "../lib/api";
import { Download, Filter, FileText, LogIn, Upload, Trash2, Edit, ShieldAlert, Key, Settings as SettingsIcon, UserPlus, UserMinus, UserCog } from "lucide-react";

const ACTION_META = {
  login: { label: "Sign in", icon: LogIn, color: "text-emerald-600" },
  sso_login: { label: "SSO Sign in", icon: ShieldAlert, color: "text-emerald-600" },
  upload_document: { label: "Upload document", icon: Upload, color: "text-blue-600" },
  delete_document: { label: "Delete document", icon: Trash2, color: "text-red-600" },
  update_document: { label: "Update document", icon: Edit, color: "text-amber-600" },
  invite_user: { label: "Invite user", icon: UserPlus, color: "text-blue-600" },
  change_role: { label: "Change role", icon: UserCog, color: "text-purple-600" },
  deactivate_user: { label: "Deactivate user", icon: UserMinus, color: "text-red-600" },
  update_aria_settings: { label: "Update ARIA config", icon: SettingsIcon, color: "text-[#2563FF]" },
  update_org_settings: { label: "Update org settings", icon: SettingsIcon, color: "text-[#2563FF]" },
  rotate_api_key: { label: "Rotate API key", icon: Key, color: "text-amber-600" },
};

const ACTIONS = ["all", ...Object.keys(ACTION_META)];

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("all");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-logs`, { params: { action, days } });
      setLogs(data);
    } catch (err) { console.error("[ARIA]", err); }
    finally { setLoading(false); }
  }, [action, days]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    const { data } = await api.get(`/audit-logs/export.pdf`, {
      params: { action, days }, responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl mb-1">Audit Log</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Every action across your lab — exportable as branded PDF for compliance.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)]">
              <Filter size={13} className="text-[var(--aria-text-muted)]" />
              <select value={action} onChange={e=>setAction(e.target.value)} data-testid="audit-action-filter"
                className="bg-transparent text-sm outline-none pr-2">
                {ACTIONS.map(a => <option key={a} value={a}>{a === "all" ? "All actions" : (ACTION_META[a]?.label || a)}</option>)}
              </select>
            </div>
            <select value={days} onChange={e=>setDays(parseInt(e.target.value))} data-testid="audit-days-filter"
              className="px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)]">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
              <option value={0}>All time</option>
            </select>
            <button onClick={exportPdf} data-testid="audit-export-pdf"
              className="flex items-center gap-2 px-4 py-1.5 text-sm rounded-md bg-[#18181B] text-white hover:bg-[#27272A]">
              <Download size={14} /> Export PDF
            </button>
          </div>
        </div>

        <div className="aria-card overflow-hidden">
          {logs.length === 0 && !loading && (
            <div className="p-12 text-center text-[var(--aria-text-muted)]">
              <FileText size={32} className="mx-auto mb-3 opacity-40" />
              <div className="text-sm">No audit entries for this filter.</div>
            </div>
          )}
          {logs.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-[#2563FF]/5 text-left text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">
                <tr>
                  <th className="px-4 py-3">When (UTC)</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => {
                  const meta = ACTION_META[l.action] || { label: l.action, icon: FileText, color: "text-slate-500" };
                  const Icon = meta.icon;
                  return (
                    <tr key={l.log_id} className="border-t border-[var(--aria-border)] hover:bg-[#2563FF]/[0.03]"
                        data-testid={`audit-row-${l.log_id}`}>
                      <td className="px-4 py-3 text-xs font-mono text-[var(--aria-text-muted)]">
                        {l.timestamp ? new Date(l.timestamp).toISOString().replace("T"," ").slice(0,19) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{l.actor_name || "—"}</div>
                        <div className="text-xs text-[var(--aria-text-muted)]">{l.actor_email || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex items-center gap-1.5 ${meta.color}`}>
                          <Icon size={13} /> <span className="text-sm">{meta.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--aria-text-muted)] max-w-[220px] truncate" title={l.target_label || l.target}>
                        {l.target_label || l.target || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--aria-text-muted)] max-w-[260px]">
                        {Object.entries(l.metadata || {}).map(([k,v]) => (
                          <span key={k} className="inline-block mr-2"><span className="opacity-70">{k}:</span> {String(v)}</span>
                        ))}
                        {l.ip_address && <span className="opacity-50 font-mono ml-1">· {l.ip_address}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
